import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import { useLang } from "@/context/LangContext";
import { useChrome } from "@/lib/guChrome";

const navItems = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/general-psychiatry", label: "Psychiatry", testid: "nav-general-psychiatry" },
  { to: "/neuropsychiatry", label: "Neuropsychiatry", testid: "nav-neuropsychiatry" },
  { to: "/deaddiction", label: "Deaddiction", testid: "nav-deaddiction" },
  { to: "/neuromodulation", label: "Neuromodulation", testid: "nav-neuromodulation" },
  { to: "/sexology", label: "Sexology", testid: "nav-sexology" },
  { to: "/case-reports", label: "Case Reports", testid: "nav-case-reports" },
  { to: "/articles", label: "Articles", testid: "nav-articles" },
  { to: "/blog", label: "Blog", testid: "nav-blog" },
  { to: "/faq", label: "FAQ", testid: "nav-faq" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const { t } = useLang();
  const { g, route } = useChrome();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[#A39171]/15"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl 2xl:max-w-[1700px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex flex-col items-start leading-none group shrink-0">
            <span className="font-display text-2xl md:text-[26px] text-[#2D2623] tracking-tight leading-none">
              Manas
            </span>
            <span className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[#A39171] hidden sm:inline whitespace-nowrap">
              {g("Since 1989")}
            </span>
          </Link>

          <nav className="hidden 2xl:flex items-center gap-5 2xl:ml-8">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={route(it.to)}
                end={it.to === "/"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `text-[12px] uppercase tracking-[0.12em] font-medium transition-colors link-underline whitespace-nowrap ${
                    isActive ? "text-[#E07A5F]" : "text-[#2D2623] hover:text-[#E07A5F]"
                  }`
                }
              >
                {g(it.label)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden 2xl:flex items-center gap-3 shrink-0 2xl:ml-6">
            <a
              href="tel:+919427560101"
              data-testid="nav-phone"
              className="flex items-center gap-2.5 text-sm text-[#2D2623] hover:text-[#E07A5F] transition-colors whitespace-nowrap"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium tracking-wide">+91 94275 60101</span>
            </a>
            <Button
              data-testid="nav-book-btn"
              onClick={() => setBookOpen(true)}
              className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-5 2xl:px-6 h-11 font-medium transition-all duration-300 whitespace-nowrap"
            >
              {g("Book Appointment")}
            </Button>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="2xl:hidden w-11 h-11 rounded-full bg-[#F4EFE6] flex items-center justify-center text-[#2D2623]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={`2xl:hidden overflow-hidden transition-all duration-500 backdrop-blur-xl bg-[#FDFBF7]/95 ${
            open ? "max-h-[640px] border-b border-[#A39171]/15" : "max-h-0"
          }`}
        >
          <nav className="px-6 py-6 flex flex-col gap-2">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={route(it.to)}
                end={it.to === "/"}
                data-testid={`mobile-${it.testid}`}
                className={({ isActive }) =>
                  `py-3 px-4 rounded-2xl text-base font-medium transition-colors ${
                    isActive
                      ? "bg-[#F4EFE6] text-[#E07A5F]"
                      : "text-[#2D2623] hover:bg-[#F4EFE6]"
                  }`
                }
              >
                {g(it.label)}
              </NavLink>
            ))}
            <div className="border-t border-[#A39171]/20 my-3" />
            <a
              href="tel:+919427560101"
              data-testid="mobile-nav-phone"
              className="py-3 px-4 rounded-2xl text-base font-medium text-[#2D2623] flex items-center gap-3"
            >
              <Phone className="w-4 h-4" /> +91 94275 60101
            </a>
            <Button
              data-testid="mobile-nav-book-btn"
              onClick={() => setBookOpen(true)}
              className="mt-2 rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] h-12 font-medium"
            >
              {g("Book Appointment")}
            </Button>
          </nav>
        </div>
      </header>

      <AppointmentDialog open={bookOpen} onOpenChange={setBookOpen} />
    </>
  );
}
